// A danışman's day in its own drawer (Onur, 14.10.2026): a tap on her
// name above the column. What to hand her today and what that number
// is made of, her seanslar with the grid's signs (a tap opens the
// seans), and her balance. Everything comes from the board already on
// the screen: the payout row is nefes_provider_checkout's payNowTL,
// never worked out here. The owner's column has no payout row: her
// line is the devir. Money only to money holders, through gizli mod.
// The hero's button opens the sheet that records the payout or the
// devir (Onur, 22.10.2026), for whoever may (admin).

import type {BoardCancelled, BoardCell, DayBoard} from "../api/contract";
import {Sheet} from "../actions/Sheets";
import {STATE_SIGN} from "../grid/signs";
import {seansState, type SeansStateKind} from "../labels";
import {Avatar} from "../ui/Avatar";
import {Icon} from "../ui/Icon";
import {useMoney} from "../ui/blind-context";

const trDay = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const round2 = (n: number): number => Math.round(n * 100) / 100;

// One line of her day: a planned seans, a cancelled one, or a record
// with no plan behind it.
interface Line {
  key: string;
  time: string;
  names: string;
  kind: SeansStateKind;
  note?: string;
  open?: () => void;
}

export function ProviderSheet({board, providerId, money, isToday, onClose, onBack, onCell,
  onCancelled, onPay, onDevir}: {
  board: DayBoard;
  providerId: string;
  money: boolean; // the caller may see TL figures
  isToday: boolean;
  onClose: () => void;
  onBack?: () => void; // opened from the Kasa drawer: back to it
  onCell: (cell: BoardCell) => void;
  onCancelled: (rec: BoardCancelled) => void;
  // Ver, for whoever may record a payout: her payout sheet, Seda's devir.
  onPay?: () => void;
  onDevir?: () => void;
}) {
  const {tl, num} = useMoney();
  const provider = board.providers.find((p) => p.providerId === providerId);
  const name = provider?.name ??
    board.totals?.payouts.due?.find((d) => d.providerId === providerId)?.name ?? "Danışman";

  const cells = board.cells.filter((c) => c.providerId === providerId);
  const lines: Line[] = [
    ...cells.map((c): Line => {
      const source = c.clients.find((x) => x.attendance.state === "confirmed")
        ?.attendance.source ?? null;
      return {
        key: `c:${c.id}`, time: c.time,
        names: c.clients.map((x) => x.name).join(", ") || c.title,
        kind: seansState(c.status, c.flags, source).kind, open: () => onCell(c),
      };
    }),
    ...board.cancelled.filter((r) => r.providerId === providerId && r.time).map((r): Line => ({
      key: `x:${r.cancellationId}`, time: r.time ?? "",
      names: r.clientNames.join(", ") || "—", kind: r.kind === "paused" ? "paused" : "cancelled",
      note: r.reason, open: () => onCancelled(r),
    })),
    ...board.unplanned.filter((u) => u.providerId === providerId && u.time).map((u): Line => ({
      key: `u:${u.serviceId}`, time: u.time ?? "", names: u.name, kind: "done", note: "plansız",
    })),
  ].sort((a, b) => a.time.localeCompare(b.time));
  const live = cells.filter((c) => c.status !== "cancelled" && c.status !== "paused");
  const unrecorded = lines.filter((l) => l.kind === "unrecorded" || l.kind === "partial").length;
  const span = live.length > 0 ?
    `${[...live].sort((a, b) => a.time.localeCompare(b.time))[0].time}–` +
    `${[...live].sort((a, b) => b.endTime.localeCompare(a.endTime))[0].endTime}` : "";
  const day = isToday ? "Bugün" : trDay(board.date);

  // Money: her payout row today, the owner's devir, what she was handed.
  const p = money ? board.totals?.payouts ?? null : null;
  const isOwner = !!p && p.ownerProviderId === providerId;
  const due = p?.due?.find((d) => d.providerId === providerId) ?? null;
  const handedTL = p ? round2(p.paid
    .filter((x) => isOwner ? x.toSeda : !x.toSeda && x.providerId === providerId)
    .reduce((a, x) => a + x.amountTL, 0)) : 0;
  // Her seanslar' fees for the day: what the pair's card says, for every
  // seans that is still on (cancelled and not-coming ones cost nothing).
  const feesTL = money ? round2(live.filter((c) => c.status !== "declined")
    .flatMap((c) => c.clients).reduce((a, x) => a + (x.money?.feeTL ?? 0), 0)) : 0;

  // Her photo big (Onur, 3.11.2026), her name and her day beside it.
  const who = (
    <div className="pd-who">
      <Avatar providerId={providerId} name={name} photo={provider?.photo} size="lg" />
      <div className="pd-who-text">
        <h2 className="sd-title pd-name">{name}</h2>
        <p className="sd-sub">
          {live.length > 0 ?
            <>{day} {live.length} seans · <span className="pd-span">{span}</span></> :
            `${day} seansı yok`}
        </p>
      </div>
    </div>
  );

  const head = (
    <header className="sd-head">
      <div className="sd-who">
        {onBack ? <>
          <button className="sd-chip sd-back" onClick={onBack}>
            <Icon name="back" /><span className="sd-ell">Kasa</span>
          </button>
          <span className="spacer" />
        </> : who}
        <button className="sd-round" aria-label="Kapat" onClick={onClose}><Icon name="x" /></button>
      </div>
      {onBack && who}
      {(unrecorded > 0 || provider?.calendarOk === false) && (
        <div className="sd-chips">
          {unrecorded > 0 && (
            <span className="sd-chip state warn"><Icon name="alert" />{unrecorded} seansın kaydı yok</span>
          )}
          {provider?.calendarOk === false && (
            <span className="sd-chip state bad"><Icon name="alert" />Takvim okunamadı</span>
          )}
        </div>
      )}
    </header>
  );

  return (
    <Sheet className="seans-sheet provider-sheet" head={head} onClose={onClose}>
      {isOwner && p?.seda && (
        <section className="dm-card pd-hero">
          <span className="dm-label">Kasadan en fazla alabilir</span>
          <Figure value={tl(p.seda.safeTL)} />
          <small>Hakedişi {tl(p.seda.owedTL)}</small>
          {onDevir && p.seda.safeTL > 0 && (
            <button className="sd-go pd-go" onClick={onDevir}>Kasadan ver</button>
          )}
        </section>
      )}
      {due && (
        <section className="dm-card pd-hero">
          <span className="dm-label">Bugün ver</span>
          <Figure value={tl(due.payNowTL)} />
          {due.unrecorded.count > 0 && (
            <small>
              Bugün {due.unrecorded.count} seans daha kaydedilince
              {due.unrecorded.estimateTL > 0 ? ` yaklaşık ${tl(due.unrecorded.estimateTL)}` : ""} eklenir.
            </small>
          )}
          {due.balanceTL < 0 && (
            <small>
              Hesabı −{num(Math.abs(due.balanceTL))}: fazla ödenmiş ya da komisyon borcu. Sohbette sorun.
            </small>
          )}
          {onPay && due.payNowTL > 0 && (
            <button className="sd-go pd-go" onClick={onPay}>{tl(due.payNowTL)} ver</button>
          )}
        </section>
      )}
      {due && due.items.length > 0 && (
        <section className="dm-card" aria-label="Bu rakam neden">
          <h3 className="dm-card-head"><span>Bu rakam neden</span></h3>
          <ul className="dm-rows">
            {due.items.map((i, n) => (
              <li key={n} className="dm-row">
                <span className="dm-k">{trDay(i.date)} {i.what.replace(/ seansı$/, "")}</span>
                <span className="dm-v">{tl(i.shareTL)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="dm-card" aria-label="Seanslar">
        <h3 className="dm-card-head"><span>{isToday ? "Bugünkü seanslar" : `${day} seansları`}</span></h3>
        <ul className="dm-rows">
          {lines.length === 0 && (
            <li className="dm-row"><span className="dm-k soft">Bu gün seansı yok</span><span /></li>
          )}
          {lines.map((l) => <SeansLine key={l.key} line={l} />)}
          {p && feesTL > 0 && (
            <li className="dm-row"><span className="dm-k">Seans ücretleri</span>
              <span className="dm-v">{tl(feesTL)}</span></li>
          )}
          {p && (isOwner || due || handedTL > 0) && (
            <li className="dm-row">
              <span className="dm-k">
                {isOwner ? (isToday ? "Bugünkü devir" : "O günkü devir") :
                  isToday ? "Bugün verilen" : "O gün verilen"}
              </span>
              <span className={handedTL > 0 ? "dm-v" : "dm-v soft"}>{tl(handedTL)}</span>
            </li>
          )}
        </ul>
      </section>

      {due && (
        <section className="dm-card" aria-label="Hesap">
          <h3 className="dm-card-head"><span>Hesap</span></h3>
          <ul className="dm-rows">
            <li className="dm-row">
              <span className="dm-k">Bakiyesi<small>sohbetteki çıkış hesabıyla aynı</small></span>
              <span className={due.balanceTL < 0 ? "dm-v bad" : "dm-v"}>
                {due.balanceTL < 0 ? `−${tl(Math.abs(due.balanceTL))}` : tl(due.balanceTL)}
              </span>
            </li>
          </ul>
        </section>
      )}
    </Sheet>
  );
}

function Figure({value}: {value: string}) {
  const [n, cur] = value.endsWith(" TL") ? [value.slice(0, -3), "TL"] : [value, ""];
  return <span className="dm-fig pd-fig">{n} {cur && <span className="cur">{cur}</span>}</span>;
}

function SeansLine({line: l}: {line: Line}) {
  const sign = STATE_SIGN[l.kind];
  const body = (
    <>
      <span className="pd-time">{l.time}</span>
      <span className={l.kind === "cancelled" || l.kind === "declined" ? "pd-names off" : "pd-names"}>
        <span className="pd-n">{l.names}</span>{l.note && <small>{l.note}</small>}
      </span>
      {l.kind === "cancelled" ? <span /> :
        <span className={`gc-sign st-${l.kind}`} aria-hidden>{sign && <Icon name={sign} />}</span>}
    </>
  );
  return (
    <li className="dm-row pd-row">
      {l.open ? <button className="pd-seans" onClick={l.open}>{body}</button> :
        <span className="pd-seans">{body}</span>}
    </li>
  );
}
